/**
 * wp-header.js — WeltPartner header flyout component
 *
 * ═══════════════════════════════════════════════════════════════
 * <header-flyout> ATTRIBUTE CONTRACT
 * ═══════════════════════════════════════════════════════════════
 *
 * ELEMENT STRUCTURE
 * -----------------
 *   <header-flyout>
 *     <!-- trigger: any element with data-flyout-trigger -->
 *     <button
 *       type="button"
 *       data-flyout-trigger
 *       aria-expanded="false"
 *       aria-haspopup="true"
 *       aria-controls="<panel-id>"
 *     >Label</button>
 *
 *     <!-- panel: any element with data-flyout-panel + matching id -->
 *     <div
 *       id="<panel-id>"
 *       data-flyout-panel
 *       hidden
 *       inert
 *     >...panel contents (Task 6/7)...</div>
 *   </header-flyout>
 *
 * TRIGGER ATTRIBUTES (required on the trigger element)
 * ─────────────────────────────────────────────────────
 *   data-flyout-trigger          — JS hook; identifies the trigger element
 *   aria-expanded="false|true"   — managed by JS; set "false" in HTML
 *   aria-haspopup="true"         — set in HTML
 *   aria-controls="<id>"         — must match the panel's id
 *
 * PANEL ATTRIBUTES (required on the panel element)
 * ─────────────────────────────────────────────────
 *   data-flyout-panel            — JS hook; identifies the panel element
 *   id="<panel-id>"              — must match trigger's aria-controls
 *   hidden                       — set in HTML (JS removes on open)
 *   inert                        — set in HTML (JS removes on open)
 *
 * OPTIONAL PANEL ATTRIBUTES
 * ─────────────────────────
 *   data-flyout-no-hover         — set on <header-flyout> to disable hover-intent
 *                                  (use for utility links that should be click-only)
 *
 * BEHAVIOUR
 * ─────────
 *   • Click trigger → toggle open/closed
 *   • Opening: aria-expanded → "true", removes hidden + inert on panel,
 *     rotates chevron (.wp-header__chevron → data-open on <header-flyout>),
 *     closes any other open <header-flyout> on the page (only 1 open at a time)
 *   • Closing: aria-expanded → "false", adds hidden + inert on panel,
 *     returns focus to trigger on Escape
 *   • Desktop hover-intent (≥990px breakpoint unless data-flyout-no-hover):
 *     opens on mouseenter of the <header-flyout> element after 120ms delay,
 *     closes on mouseleave after 200ms delay (moving between trigger & panel
 *     doesn't close it)
 *   • Close on: Escape key (focus returns to trigger), click outside flyout
 *   • Respects prefers-reduced-motion: skips CSS transition class when set
 *   • Does NOT trap focus (menu pattern, not modal)
 *   • Tab moves into panel naturally when open
 *
 * CHEVRON ROTATION
 * ────────────────
 *   Add class .wp-header__chevron to the SVG wrapper inside the trigger.
 *   The CSS in app.css rotates it 180° when [data-open] is on <header-flyout>.
 *   Example: <span class="wp-header__chevron" aria-hidden="true">▾</span>
 *
 * DOWNSTREAM USAGE (Tasks 6 & 7)
 * ──────────────────────────────
 *   Task 6 (Sortiment flyout panel): populate #wp-sortiment-flyout contents;
 *     the <header-flyout> wrapper + trigger are already wired in header.liquid.
 *   Task 7 (Utility flyouts): wrap each utility link + its panel in a
 *     <header-flyout> element; use the same data-flyout-trigger / data-flyout-panel
 *     attribute contract.
 *
 * ═══════════════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  const DESKTOP_MQ = window.matchMedia('(min-width: 990px)');
  const OPEN_DELAY = 120;   // ms before opening on hover
  const CLOSE_DELAY = 200;  // ms before closing on hover-leave

  class HeaderFlyout extends HTMLElement {
    constructor() {
      super();
      this._openTimer = null;
      this._closeTimer = null;
      this._isOpen = false;
      this._boundOnKeydown = this._onKeydown.bind(this);
      this._boundOnClickOutside = this._onClickOutside.bind(this);
      this._boundOnMouseEnter = this._onMouseEnter.bind(this);
      this._boundOnMouseLeave = this._onMouseLeave.bind(this);
      this._openRafId = null;
    }

    connectedCallback() {
      this._ac = new AbortController();
      const { signal } = this._ac;

      this._trigger = this.querySelector('[data-flyout-trigger]');
      this._panel = this.querySelector('[data-flyout-panel]');

      if (!this._trigger || !this._panel) return;

      // Ensure panel starts closed
      this._panel.hidden = true;
      this._panel.inert = true;
      this._trigger.setAttribute('aria-expanded', 'false');

      this._trigger.addEventListener('click', () => this._toggle(), { signal });

      // Hover-intent (desktop only, unless opted out)
      if (!this.hasAttribute('data-flyout-no-hover')) {
        this.addEventListener('mouseenter', this._boundOnMouseEnter, { signal });
        this.addEventListener('mouseleave', this._boundOnMouseLeave, { signal });
      }
    }

    disconnectedCallback() {
      this._ac?.abort();
      document.removeEventListener('keydown', this._boundOnKeydown);
      document.removeEventListener('click', this._boundOnClickOutside);
    }

    /** Open the flyout */
    open() {
      if (this._isOpen) return;

      // Close all other open flyouts (only one open at a time)
      document.querySelectorAll('header-flyout').forEach(el => {
        if (el !== this && el._isOpen) el.close();
      });
      // One header panel at a time: also close an open predictive-search dropdown
      document.querySelectorAll('predictive-search[open]').forEach((ps) => {
        if (typeof ps.close === 'function') ps.close();
      });

      this._isOpen = true;
      this.setAttribute('data-open', '');

      this._panel.hidden = false;
      this._panel.inert = false;
      this._trigger.setAttribute('aria-expanded', 'true');

      // Respect prefers-reduced-motion
      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!prefersReduced) {
        // Small rAF so transition triggers after hidden is removed
        if (this._openRafId !== null) cancelAnimationFrame(this._openRafId);
        this._openRafId = requestAnimationFrame(() => {
          this._openRafId = null;
          this._panel.setAttribute('data-visible', '');
        });
      } else {
        this._panel.setAttribute('data-visible', '');
      }

      document.addEventListener('keydown', this._boundOnKeydown);
      document.addEventListener('click', this._boundOnClickOutside);
    }

    /** Close the flyout */
    close(returnFocus = false) {
      if (!this._isOpen) return;

      if (this._openRafId !== null) {
        cancelAnimationFrame(this._openRafId);
        this._openRafId = null;
      }

      this._isOpen = false;
      this.removeAttribute('data-open');

      this._panel.removeAttribute('data-visible');
      this._panel.hidden = true;
      this._panel.inert = true;
      this._trigger.setAttribute('aria-expanded', 'false');

      document.removeEventListener('keydown', this._boundOnKeydown);
      document.removeEventListener('click', this._boundOnClickOutside);

      if (returnFocus && this._trigger) {
        this._trigger.focus();
      }
    }

    _toggle() {
      this._clearTimers();
      if (this._isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    _onKeydown(event) {
      if (event.key === 'Escape') {
        this.close(true);
      }
    }

    _onClickOutside(event) {
      if (!this.contains(event.target)) {
        this.close();
      }
    }

    _onMouseEnter() {
      if (!DESKTOP_MQ.matches) return;
      this._clearTimers();
      if (!this._isOpen) {
        this._openTimer = setTimeout(() => this.open(), OPEN_DELAY);
      }
    }

    _onMouseLeave() {
      if (!DESKTOP_MQ.matches) return;
      this._clearTimers();
      if (this._isOpen) {
        this._closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
      }
    }

    _clearTimers() {
      if (this._openTimer !== null) {
        clearTimeout(this._openTimer);
        this._openTimer = null;
      }
      if (this._closeTimer !== null) {
        clearTimeout(this._closeTimer);
        this._closeTimer = null;
      }
    }
  }

  customElements.define('header-flyout', HeaderFlyout);
})();

/**
 * Drawer main-close (X) fix — Asana 1217188659244210
 *
 * Dawn's MenuDrawer.bindEvents() (global.js) wires EVERY <button> inside the
 * drawer to onCloseButtonClick() → closeSubmenu(closest('details')). For the
 * main X in the drawer header, closest('details') is the main drawer toggle,
 * so the drawer closes visually but closeMenuDrawer() never runs: body keeps
 * overflow-hidden-<breakpoint> and the page can no longer be scrolled.
 * (closeMenuDrawer() early-returns without an event, so no other path cleans
 * up either.)
 *
 * Fix without touching Dawn core: intercept the X click in the capture phase
 * (fires before Dawn's bubble listener on the button), run the real
 * closeMenuDrawer(event, summary) path, and stop propagation so Dawn's
 * closeSubmenu() misroute never fires. Submenu back-buttons
 * (.wp-drawer__back-btn) are not matched and keep Dawn's stock behaviour.
 */
(function () {
  'use strict';

  document.addEventListener(
    'click',
    function (event) {
      const closeButton = event.target.closest('.wp-drawer__close');
      if (!closeButton) return;

      const drawer = closeButton.closest('header-drawer, menu-drawer');
      // Not a live drawer (e.g. the static wp-drawer-demo replica) or not
      // open → leave the click alone.
      if (!drawer || typeof drawer.closeMenuDrawer !== 'function' || !drawer.mainDetailsToggle) return;
      if (!drawer.mainDetailsToggle.hasAttribute('open')) return;

      event.stopPropagation();

      const summary = drawer.mainDetailsToggle.querySelector('summary');
      drawer.closeMenuDrawer(event, summary);
      // Dawn only resets aria-expanded for KeyboardEvents; keep the burger
      // summary's state correct for pointer/touch closes too.
      if (summary) summary.setAttribute('aria-expanded', 'false');
    },
    true
  );
})();
